import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthAPI } from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";

// Interface for user data from validate endpoint
interface ValidateUserResponse {
  username: string;
  email: string;
  subscription_type: string;
  last_paid_subscription?: string;
  role?: string; // User role for admin access
  manage_orgs?: boolean;
  // Quota information returned by backend
  project_file_limit: number;
  project_files_used: number;
  global_file_limit: number;
  global_files_used: number;
}

// Shared validation hook to prevent duplicate API calls
export const useValidation = () => {
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["auth", "validate"],
    queryFn: async (): Promise<ValidateUserResponse> => {
      try {
        const response = await AuthAPI.validateAuth0Token();
        return response;
      } catch (error) {
        navigate("/login");
        throw error;
      }
    },
    enabled: isAuthenticated && !!token && !authLoading, // Only fetch when authenticated with token
    // Don't refetch on window focus for validation
    refetchOnWindowFocus: false,
    // Cache validation for 10 minutes to reduce API calls
    staleTime: 10 * 60 * 1000,
    // Keep data fresh for 15 minutes
    gcTime: 15 * 60 * 1000,
    // Retry failed requests with exponential backoff
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors (auth issues)
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as any).status;
        if (status === 401 || status === 403) {
          return false;
        }
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
};

export type { ValidateUserResponse };
